import React, { useState } from 'react'
import s from '@commonStyles/footer/Footer.module.scss'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'
import { Newsletter } from 'stores/ooka_uae_store/components/alfakher/pylot-footer-newsletter'
import { NavigationResponse } from '@pylot-data/hooks/cms/use-cms-blocks'
import { Maybe, CmsBlock } from '@pylot-data/fwrdschema'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import cn from 'classnames'
import { usePreloadedData } from '@preload'
import { FOOTER_CMS_BLOCKS } from '@config/preload'
import { ClickOutside } from '@corratech/pylot-utils'
import { useRouter } from 'next/router'
import { EnglishLogo, ArabicLogo } from 'stores/ooka_default/components/icons'
import StoreSwitcher from 'stores/ooka_uae_store/components/common/StoreSwitcher/StoreSwitcher'

const Footer = (): JSX.Element | null => {
    const config = useStoreConfig()
    const { footer: { role } = {} } = config.preload
    const router = useRouter()
    const [displayLanguageDropdown, setDisplayLanguageDropdown] = useState(
        false
    )
    const isCheckout = router?.pathname?.includes('/checkout')

    const {
        data: {
            cmsBlocks: { items }
        }
    } = (usePreloadedData(FOOTER_CMS_BLOCKS) as unknown) as {
        data: { cmsBlocks: NavigationResponse }
    }

    if (!items || !role) {
        return null
    }

    const mappedItems: Record<string, Maybe<CmsBlock>> = {}
    items.forEach((item) => {
        if (item && item.identifier && item.content) {
            mappedItems[item.identifier] = item
        }
    })

    let logoComponent: JSX.Element | null = null
    if (router.locale === 'en') {
        logoComponent = <ArabicLogo />
    } else {
        logoComponent = <EnglishLogo />
    }

    const handleopenChat = document.getElementById('handlechat')
    if (handleopenChat) {
        const openChatHandler = () => {
            if (typeof window !== 'undefined' && window.HubSpotConversations) {
                window.HubSpotConversations.widget.open()
            }
        }

        handleopenChat.addEventListener('click', openChatHandler)
        window.removeEventListener('click', openChatHandler)
    }

    const getBlockOutput = (identifier?: string | null, className?: string) => {
        if (identifier && mappedItems[identifier]) {
            const shouldRenderStoreSwitcher = className === 'language'
            return (
                <div className={className ? s[className] : ''}>
                    <RichContent html={mappedItems[identifier]?.content} />
                    {shouldRenderStoreSwitcher && (
                        <div className={s['footer-language-switch']}>
                            <ClickOutside
                                active={displayLanguageDropdown}
                                onClick={() =>
                                    setDisplayLanguageDropdown(false)
                                }
                            >
                                <div className={s['footer-language-switcher']}>
                                    {logoComponent}
                                    <StoreSwitcher footerSwitcher />
                                </div>
                            </ClickOutside>
                        </div>
                    )}
                </div>
            )
        }
        return null
    }
    if (isCheckout) {
        return (
            <footer className={cn(s.footer, s['checkout-footer'])}>
                <div className={s['footer-container']}>
                    <div className={cn(s['copyright'])}>
                        {getBlockOutput(role.copyright)}
                    </div>
                </div>
            </footer>
        )
    }

    return (
        <footer className={s.footer}>
            <div className={s['footer-value-prop-row']}>
                {getBlockOutput(role.unique)}
            </div>

            <div className={s['footer-container']}>
                <div
                    className={cn(
                        s['grid-rows'],
                        s['social-block'],
                        'flex-wrap'
                    )}
                >
                    <div id="news-letter" className={s['grid-col-1']}>
                        <Newsletter />
                    </div>
                    <div className={s['payment-split-wrapper']}>
                        {getBlockOutput('footer_payment_split')}
                    </div>
                    <div className={s['grid-col-2']}>
                        {getBlockOutput('footer-social-media')}
                    </div>
                </div>
                <div className={s['footer-links-block']}>
                    {getBlockOutput(role.links)}
                </div>
                <div className={s['secure-payment']}>
                    {getBlockOutput('footer-secure-payment')}
                </div>
                <div className={s['copyright']}>
                    {getBlockOutput(role.copyright)}
                </div>
            </div>
        </footer>
    )
}

export default Footer
